import { useState, useEffect, useRef } from 'react';
import { DEFAULT_CONFIG } from './constants';
import { loadConfigFromServer, saveConfigToServer, disconnectFromServer } from './api';

export const useConfigForm = (isOpen, fullScreen, onClose) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const onCloseRef = useRef(onClose);
  const hasLoadedRef = useRef(false);
  const saveTimeoutRef = useRef(null);

  // Keep onClose ref updated
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Merge config helper
  const mergeConfig = (prev, parsed) => ({
    ...prev,
    customChecks: { ...prev.customChecks, ...parsed.customChecks },
    autoBurn: { ...prev.autoBurn, ...parsed.autoBurn },
    eslint: { ...prev.eslint, ...parsed.eslint },
    typescript: { ...prev.typescript, ...parsed.typescript },
    lineLimit: { ...prev.lineLimit, ...parsed.lineLimit },
    folderLimit: { ...prev.folderLimit, ...parsed.folderLimit },
    testCheck: { ...prev.testCheck, ...parsed.testCheck, patterns: { ...prev.testCheck?.patterns, ...parsed.testCheck?.patterns } }
  });

  // Load config when modal opens or in fullScreen mode
  const loadConfig = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const loadedConfig = await loadConfigFromServer();
      setConfig(prev => mergeConfig(prev, loadedConfig));
      setIsConnected(true);
    } catch (err) {
      console.error('Failed to load config:', err);
      setIsConnected(false);
    } finally {
      setIsLoading(false);
      // Mark as loaded after a brief delay to avoid triggering auto-save immediately
      setTimeout(() => {
        hasLoadedRef.current = true;
      }, 100);
    }
  };

  useEffect(() => {
    if (isOpen || fullScreen) {
      loadConfig();
    }
  }, [isOpen, fullScreen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Silent save (no loading state, just saves in background)
  const saveConfigSilent = async () => {
    try {
      await saveConfigToServer(config);
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  };

  // Auto-save config when it changes (debounced)
  useEffect(() => {
    if (!hasLoadedRef.current) return;

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce the save by 500ms
    saveTimeoutRef.current = setTimeout(() => {
      saveConfigSilent();
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [config]);

  const saveConfig = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await saveConfigToServer(config);
      onClose();
    } catch (err) {
      console.error('Failed to save config:', err);
      setError('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const disconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnectFromServer();
    } catch (err) {
      console.error('Error disconnecting:', err);
    } finally {
      setIsDisconnecting(false);
    }
  };

  // Handle "Get Started" - save config, show success, and disconnect with countdown
  const handleGetStarted = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    setCountdown(null);

    try {
      // Save config first
      await saveConfigSilent();

      // Show success message and start countdown
      setSuccessMessage('Settings saved, return to your terminal');
      setCountdown(5);
      setIsSaving(false);

      // Countdown from 5 to 1, then disconnect
      let count = 5;
      const countdownInterval = setInterval(() => {
        count--;
        if (count > 0) {
          setCountdown(count);
        } else {
          clearInterval(countdownInterval);
          setCountdown(null);
          setSuccessMessage(null);
          setIsConnected(false);
          disconnect();
        }
      }, 1000);
    } catch (err) {
      console.error('Failed to save and disconnect:', err);
      setError('Failed to save configuration');
      setIsSaving(false);
    }
  };

  return {
    config,
    setConfig,
    isLoading,
    isSaving,
    isDisconnecting,
    error,
    successMessage,
    countdown,
    isConnected,
    saveConfig,
    handleGetStarted
  };
};
